import { useEffect, useRef, useState } from "react";
import { Check, Copy, Mail, QrCode, Send } from "lucide-react";
import { qrDoLink } from "@/lib/link-do-catalogo";

function formatPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Tela de resultado de um convite gerado — link, WhatsApp, email, QR e validade.
 *
 * Morava dentro de PatientInviteLinkDialog e era importada de lá por outros quatro diálogos, o que
 * acoplava qualquer mudança no fluxo de paciente a todos eles. Extraída pra cá antes de o convite
 * de parceria virar o quinto usuário.
 *
 * O QR é gerado aqui mesmo (qrcode-generator): antes ia para o api.qrserver.com, levando o token do
 * convite a um terceiro.
 */
export function ResultStage({
  inviteUrl,
  phone,
  email,
  whatsappHref,
  mailtoHref,
  copied,
  onCopy,
  showQr,
  onToggleQr,
  expiryLabel,
  onNewLink,
  onClose,
}: {
  inviteUrl: string;
  phone: string | null;
  email: string | null;
  whatsappHref: string;
  mailtoHref: string;
  copied: boolean;
  onCopy: () => void;
  showQr: boolean;
  onToggleQr: () => void;
  expiryLabel: string | null;
  onNewLink: () => void;
  onClose: () => void;
}) {
  const hasPhone = !!phone;
  const hasEmail = !!email;

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-gradient-to-br from-brand-50 to-brand-100/50 p-3 text-center dark:from-primary/15 dark:to-primary/5">
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-700 dark:text-primary">
          Link de cadastro
        </div>
        <div className="select-all break-all font-mono text-[12.5px] text-zinc-800 dark:text-foreground">
          {inviteUrl}
        </div>
      </div>

      {(hasPhone || hasEmail) && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-2.5 text-[11.5px] leading-relaxed text-brand-900 dark:border-primary/30 dark:bg-primary/5 dark:text-primary">
          {hasPhone && <div>WhatsApp: {formatPhone(phone!)}</div>}
          {hasEmail && <div>E-mail: {email}</div>}
          <div className="mt-0.5 text-[10.5px] text-brand-700/80 dark:text-primary/80">
            O formulário vai abrir pré-preenchido.
          </div>
        </div>
      )}

      {showQr && <QrBlock url={inviteUrl} />}

      <div className="space-y-1.5">
        {hasPhone && (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-emerald-500 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
          >
            <Send className="h-4 w-4" />
            Enviar no WhatsApp de {formatPhone(phone!)}
          </a>
        )}
        {hasEmail && (
          <a
            href={mailtoHref}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-blue-600 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            <Mail className="h-4 w-4" />
            Enviar por e-mail para {email}
          </a>
        )}
        {!hasPhone && !hasEmail && (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-emerald-500 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
          >
            <Send className="h-4 w-4" />
            Compartilhar no WhatsApp
          </a>
        )}

        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                Copiado
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copiar link
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onToggleQr}
            className={
              showQr
                ? "inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-brand-500 bg-brand-50 text-[12.5px] font-semibold text-brand-800 dark:border-primary dark:bg-primary/10 dark:text-primary"
                : "inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted"
            }
          >
            <QrCode className="h-3.5 w-3.5" />
            {showQr ? "Esconder QR" : "Mostrar QR"}
          </button>
        </div>
      </div>

      {expiryLabel && (
        <p className="text-center text-[11px] text-zinc-500 dark:text-muted-foreground">
          Válido {expiryLabel}.
        </p>
      )}

      <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
        <button
          type="button"
          onClick={onNewLink}
          className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          Gerar outro
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export function DialogField({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block rounded-lg border border-zinc-200 bg-white px-3 py-2 transition-colors focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 dark:border-border dark:bg-background">
      <span className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </span>
      {children}
    </label>
  );
}

export function formatExpiry(expiresAt: Date): string {
  const ms = expiresAt.getTime() - Date.now();
  if (ms <= 0) return "expirado";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return `por ${hours}h`;
  const days = Math.round(hours / 24);
  return `por ${days} dias`;
}

function QrBlock({ url }: { url: string }) {
  const ref = useRef<HTMLImageElement>(null);
  const [errored, setErrored] = useState(false);
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrDoLink(url))}`;

  useEffect(() => {
    setErrored(false);
  }, [url]);

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-zinc-200 bg-white p-3 dark:border-border dark:bg-background">
      {errored ? (
        <div className="flex h-[180px] w-[180px] flex-col items-center justify-center rounded-md border border-dashed border-zinc-300 p-4 text-center text-[11px] text-zinc-500 dark:border-border dark:text-muted-foreground">
          Não foi possível carregar o QR code.
        </div>
      ) : (
        <img
          ref={ref}
          src={src}
          alt="QR code do link de cadastro"
          width={180}
          height={180}
          onError={() => setErrored(true)}
          className="h-[180px] w-[180px] rounded-md"
        />
      )}
      <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
        Aponte a câmera do celular do paciente.
      </p>
    </div>
  );
}
